import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // Dashboard
  getDashboard: () => api.get('/dashboard'),
  
  // Questions
  getQuestions: (subject, amount = 5) => api.get(`/questions/${subject}`, { params: { amount } }),
  
  // Answers
  submitAnswer: (data) => api.post('/answers/submit', data),
  
  // Progress
  getProgress: () => api.get('/progress'),
  
  // Leaderboard
  getLeaderboard: () => api.get('/leaderboard'),
  
  // Reflection
  submitReflection: (data) => api.post('/reflection', data),
  
  // Daily Challenge
  getDailyChallenge: () => api.get('/daily-challenge'),
  
  // Chapters
  getChapters: (subject) => api.get(`/chapters/${subject}`),
  getChapterTest: (subject, chapterId) => api.get(`/chapters/${subject}/${chapterId}/test`),
  submitChapterTest: (data) => api.post('/chapters/test/submit', data),
  
  // AI Tutor
  tutorChat: (data) => api.post('/tutor/chat', data),
  getTutorHistory: (chapterId) => api.get(`/tutor/history/${chapterId}`),
  
  // Avatar & Profile
  updateAvatar: (data) => api.put('/profile/avatar', data),
  
  // Worksheet
  generateWorksheet: (data) => api.post('/worksheet/generate', data),
  
  // Upcoming Tests
  addUpcomingTest: (data) => api.post('/tests/upcoming', data),
  getUpcomingTests: () => api.get('/tests/upcoming'),
  deleteUpcomingTest: (id) => api.delete(`/tests/upcoming/${id}`),
  
  // Timetable
  generateTimetable: (data) => api.post('/timetable/generate', data),
  
  // Emotional Check-in
  emotionalCheckin: (data) => api.post('/emotional/checkin', data),
  getEmotionalHistory: () => api.get('/emotional/history'),
  
  // Stickers
  getStickers: () => api.get('/stickers'),
  
  // Grade-specific game questions
  getGradeGameQuestions: (gameType) => api.get(`/games/grade-questions/${gameType}`),
  
  // Voice Tutor (Whisper STT + Conversational AI)
  transcribeAudio: (formData) => api.post('/voice/transcribe', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  voiceChat: (data) => api.post('/voice/chat', data),
  getVoiceHistory: () => api.get('/voice/history'),
  
  // Adaptive Engine / Deep Analytics
  getDeepAnalytics: () => api.get('/analytics/deep'),
  getRecommendation: () => api.get('/analytics/recommendation'),
  
  // ===== SCHOOL / TEACHER / PARENT =====
  registerSchoolUser: (data) => api.post('/school/register', data),
  createClass: (data) => api.post('/school/class/create', data),
  joinClass: (data) => api.post('/school/class/join', data),
  myClasses: () => api.get('/school/class/my'),
  classStudents: (classId) => api.get(`/school/class/${classId}/students`),
  studentProgress: (studentId) => api.get(`/school/student/${studentId}/progress`),
  parentLink: (data) => api.post('/school/parent/link', data),
  parentChildren: () => api.get('/school/parent/children'),
  
  // ===== HOMEWORK OCR =====
  scanHomework: (formData) => api.post('/homework/scan', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  homeworkHistory: () => api.get('/homework/history'),
  deleteHomework: (id) => api.delete(`/homework/${id}`),
  
  // ===== BOOKS LIBRARY =====
  uploadBook: (formData) => api.post('/books/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  myBooks: () => api.get('/books/my'),
  getBook: (id) => api.get(`/books/${id}`),
  bookSummary: (id) => api.post(`/books/${id}/summary`),
  askBook: (id, data) => api.post(`/books/${id}/ask`, data),
  bookQuiz: (id) => api.post(`/books/${id}/quiz`),
  deleteBook: (id) => api.delete(`/books/${id}`),
  
  // ===== SUBSCRIPTION =====
  subscriptionStatus: () => api.get('/subscription/status'),
  claimPayment: (data) => api.post('/subscription/claim-payment', data),
  myPayments: () => api.get('/subscription/payments'),
  
  // ===== ASSIGNMENTS =====
  createAssignment: (data) => api.post('/assignments/create', data),
  myAssignments: () => api.get('/assignments/my'),
  completeAssignment: (id) => api.post(`/assignments/${id}/complete`),
  deleteAssignment: (id) => api.delete(`/assignments/${id}`),
  
  // ===== CLASS ANALYTICS =====
  classAnalytics: (classId) => api.get(`/school/class/${classId}/analytics`),
  
  // ===== COMPETITIONS =====
  uploadCompetition: (formData) => api.post('/competitions/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  myCompetitions: () => api.get('/competitions/my'),
  getCompetition: (id) => api.get(`/competitions/${id}`),
  deleteCompetition: (id) => api.delete(`/competitions/${id}`),
  
  // ===== SOLVED HOMEWORK =====
  uploadSolvedHomework: (formData) => api.post('/homework/solved', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  
  // ===== ADMIN =====
  adminStats: () => api.get('/admin/stats'),
  adminPendingPayments: () => api.get('/admin/payments/pending'),
  adminAllPayments: () => api.get('/admin/payments/all'),
  adminVerifyPayment: (claimId, data) => api.post(`/admin/payments/${claimId}/verify`, data),
  
  // ===== SORA 2 VIDEO =====
  generateVideo: (data) => api.post('/video/generate', data),
  getVideoStatus: (id) => api.get(`/video/${id}`),
  listMyVideos: () => api.get('/video/my/list'),
  
  // ===== CLASS CHALLENGES =====
  createChallenge: (data) => api.post('/challenges/create', data),
  activeChallenges: () => api.get('/challenges/active'),
  endChallenge: (id) => api.delete(`/challenges/${id}`),
  
  // ===== PARENT DIGEST (mocked email) =====
  generateParentDigest: () => api.post('/parent/digest/generate'),
  parentDigestHistory: () => api.get('/parent/digest/history'),
};

export default api;